import React from 'react';
import { Box, Container, Typography, Card, CardContent, Grid, useTheme, useMediaQuery, Chip } from '@mui/material';
import { motion } from 'framer-motion';
import BusinessIcon from '@mui/icons-material/Business';
import BuildIcon from '@mui/icons-material/Build';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AssessmentIcon from '@mui/icons-material/Assessment';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import SupportIcon from '@mui/icons-material/Support';

const WestAfricaExpertise = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const whatIDeliver = [
    {
      icon: <BusinessIcon sx={{ fontSize: 40 }} />,
      title: 'Market Entry Strategy',
      description: 'In-depth analysis, partner identification, and regulatory navigation',
      gradient: 'linear-gradient(90deg, #0968E5, #091970)',
    },
    {
      icon: <BuildIcon sx={{ fontSize: 40 }} />,
      title: 'Product Development',
      description: 'Design, sourcing, and quality-assured production setup',
      gradient: 'linear-gradient(90deg, #0968E5, #091970)',
    },
    {
      icon: <TrendingUpIcon sx={{ fontSize: 40 }} />,
      title: 'Marketing & Distribution',
      description: 'Localized branding, channel development, and demand generation',
      gradient: 'linear-gradient(90deg, #0968E5, #091970)',
    },
    {
      icon: <AssessmentIcon sx={{ fontSize: 40 }} />,
      title: 'Profit Optimization',
      description: 'Cost-efficient operations, risk mitigation, and sustainable growth planning',
      gradient: 'linear-gradient(90deg, #0968E5, #091970)',
    },
  ];

  const whyChooseMe = [
    {
      icon: <LocationOnIcon sx={{ fontSize: 40 }} />,
      title: 'Deep Regional Insight',
      description: 'Longstanding relationships with local suppliers, logistics providers, and authorities',
      gradient: 'linear-gradient(90deg, #0968E5, #091970)',
    },
    {
      icon: <EmojiEventsIcon sx={{ fontSize: 40 }} />,
      title: 'Track Record of Success',
      description: 'Proven launches and profitable exits in steel fabrication and plywood manufacturing',
      gradient: 'linear-gradient(90deg, #0968E5, #091970)',
    },
    {
      icon: <SupportIcon sx={{ fontSize: 40 }} />,
      title: 'End-to-End Support',
      description: 'From concept to commercialization, ensuring seamless entry for new ventures',
      gradient: 'linear-gradient(90deg, #0968E5, #091970)',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const cardVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.6 },
    },
  };

  return (
    <Box
      sx={{
        py: { xs: 6, md: 10 },
        background: 'linear-gradient(180deg, #ffffff 0%, #f5f5f5 100%)',
      }}
    >
      <Container maxWidth="lg">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Chip
              label="West Africa Expertise"
              sx={{
                background: 'linear-gradient(90deg, #0968E5, #091970)',
                color: 'white',
                fontWeight: 600,
                fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.85rem', lg: '0.9rem' },
                px: { xs: 1.5, md: 2 },
                py: { xs: 2, md: 3 },
                mb: { xs: 1.5, md: 2 },
              }}
            />
            <Typography
              variant={isMobile ? 'h4' : 'h3'}
              sx={{
                fontWeight: 700,
                mb: { xs: 1.5, md: 2 },
                background: 'linear-gradient(90deg, #0968E5, #091970)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.25rem', lg: '2.5rem' },
              }}
            >
              Expertise in West Africa Manufacturing & Market Entry
            </Typography>
            <Typography
              variant={isMobile ? 'body1' : 'h6'}
              sx={{
                color: '#666',
                maxWidth: '800px',
                mx: 'auto',
                fontSize: { xs: '0.875rem', sm: '0.95rem', md: '1.05rem', lg: '1.2rem' },
                lineHeight: { xs: 1.6, md: 1.7 },
              }}
            >
              I possess extensive expertise, proven experience, and strong regional connections to strategically launch
              and scale manufacturing businesses across West Africa, with a focus on{' '}
              <Box component="span" sx={{ fontWeight: 700, color: '#0968E5' }}>Steel and Plywood products</Box>.
            </Typography>
          </Box>

          {/* What I Deliver Section */}
          <Box sx={{ mb: 8 }}>
            <Typography
              variant={isMobile ? 'h5' : 'h4'}
              sx={{
                textAlign: 'center',
                mb: { xs: 3, sm: 3.5, md: 4 },
                fontWeight: 700,
                color: '#333',
                fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem', lg: '2rem' },
              }}
            >
              What I Deliver
            </Typography>
            <Grid container spacing={3}>
              {whatIDeliver.map((item, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <motion.div variants={cardVariants}>
                    <Card
                      sx={{
                        height: '100%',
                        minHeight: { xs: '220px', sm: '240px' },
                        borderRadius: 4,
                        background: 'rgba(255, 255, 255, 0.95)',
                        border: '1px solid rgba(9, 104, 229, 0.1)',
                        boxShadow: '0 4px 20px rgba(9, 104, 229, 0.08)',
                        transition: 'all 0.3s ease',
                        display: 'flex',
                        flexDirection: 'column',
                        '&:hover': {
                          transform: 'translateY(-8px)',
                          boxShadow: '0 12px 40px rgba(9, 104, 229, 0.2)',
                          border: '1px solid rgba(9, 104, 229, 0.3)',
                        },
                      }}
                    >
                      <CardContent
                        sx={{
                          p: { xs: 2.5, md: 3 },
                          textAlign: 'center',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          height: '100%',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <Box
                            sx={{
                              width: { xs: 60, sm: 65, md: 70 },
                              height: { xs: 60, sm: 65, md: 70 },
                              borderRadius: '50%',
                              background: item.gradient,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              mb: { xs: 1.5, md: 2 },
                              boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                              '& svg': {
                                display: 'block',
                                margin: 'auto',
                                fontSize: { xs: 32, sm: 36, md: 40 },
                              },
                            }}
                          >
                            {item.icon}
                          </Box>
                          <Typography
                            variant={isMobile ? 'h6' : 'h6'}
                            sx={{
                              fontWeight: 700,
                              mb: { xs: 1, md: 1.5 },
                              color: '#333',
                              fontSize: { xs: '0.875rem', sm: '0.95rem', md: '1rem', lg: '1.1rem' },
                            }}
                          >
                            {item.title}
                          </Typography>
                        </Box>
                        <Typography
                          variant={isMobile ? 'body2' : 'body2'}
                          sx={{
                            color: '#666',
                            fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.85rem', lg: '0.9rem' },
                            lineHeight: { xs: 1.5, md: 1.6 },
                          }}
                        >
                          {item.description}
                        </Typography>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Why Choose Me Section */}
          <Box sx={{ mb: 6 }}>
            <Typography
              variant={isMobile ? 'h5' : 'h4'}
              sx={{
                textAlign: 'center',
                mb: { xs: 3, sm: 3.5, md: 4 },
                fontWeight: 700,
                color: '#333',
                fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem', lg: '2rem' },
              }}
            >
              Why Choose Me?
            </Typography>
            <Grid container spacing={3}>
              {whyChooseMe.map((item, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <motion.div variants={cardVariants}>
                    <Card
                      sx={{
                        height: '100%',
                        minHeight: { xs: '220px', sm: '240px' },
                        borderRadius: 4,
                        background: 'rgba(255, 255, 255, 0.95)',
                        border: '1px solid rgba(9, 104, 229, 0.1)',
                        boxShadow: '0 4px 20px rgba(9, 104, 229, 0.08)',
                        transition: 'all 0.3s ease',
                        display: 'flex',
                        flexDirection: 'column',
                        '&:hover': {
                          transform: 'translateY(-8px)',
                          boxShadow: '0 12px 40px rgba(9, 104, 229, 0.2)',
                          border: '1px solid rgba(9, 104, 229, 0.3)',
                        },
                      }}
                    >
                      <CardContent
                        sx={{
                          p: { xs: 2.5, md: 3 },
                          textAlign: 'center',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          height: '100%',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <Box
                            sx={{
                              width: { xs: 60, sm: 65, md: 70 },
                              height: { xs: 60, sm: 65, md: 70 },
                              borderRadius: '50%',
                              background: item.gradient,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              mb: { xs: 1.5, md: 2 },
                              boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                              '& svg': {
                                display: 'block',
                                margin: 'auto',
                                fontSize: { xs: 32, sm: 36, md: 40 },
                              },
                            }}
                          >
                            {item.icon}
                          </Box>
                          <Typography
                            variant={isMobile ? 'h6' : 'h6'}
                            sx={{
                              fontWeight: 700,
                              mb: { xs: 1, md: 1.5 },
                              color: '#333',
                              fontSize: { xs: '0.875rem', sm: '0.95rem', md: '1rem', lg: '1.1rem' },
                            }}
                          >
                            {item.title}
                          </Typography>
                        </Box>
                        <Typography
                          variant={isMobile ? 'body2' : 'body2'}
                          sx={{
                            color: '#666',
                            fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.85rem', lg: '0.9rem' },
                            lineHeight: { xs: 1.5, md: 1.6 },
                          }}
                        >
                          {item.description}
                        </Typography>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Call to Action */}
          <Box
            sx={{
              textAlign: 'center',
              p: { xs: 3, md: 4 },
              borderRadius: 4,
              background: 'linear-gradient(135deg, rgba(9, 104, 229, 0.05) 0%, rgba(9, 25, 112, 0.05) 100%)',
              border: '2px solid rgba(9, 104, 229, 0.15)',
            }}
          >
            <Typography
              variant={isMobile ? 'h5' : 'h4'}
              sx={{
                fontWeight: 700,
                mb: { xs: 1.5, md: 2 },
                color: '#333',
                fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem', lg: '1.75rem' },
              }}
            >
              Ready to transform your West Africa manufacturing ambitions into reality?
            </Typography>
            <Typography
              variant={isMobile ? 'body1' : 'h6'}
              sx={{
                color: '#666',
                fontSize: { xs: '0.875rem', sm: '0.95rem', md: '1.05rem', lg: '1.2rem' },
                fontWeight: 400,
              }}
            >
              Let's connect and deliver growth, quality, and profitability—your trusted partner in West Africa
            </Typography>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default WestAfricaExpertise;

